import {DBSchema, IDBPDatabase, IDBPTransaction, openDB, StoreNames, unwrap} from 'idb';
import {isNull} from 'lodash';
import {Subject} from 'rxjs';

import {ErrorWithData, ExtendedError} from '../../libs/shared/core/src/helpers/types/customErrors';
import {reportToBugsnag} from '../../libs/shared/core/src/services/bugsnag/bugsnag';
import {withTimeoutAsync} from '../../src/helpers/asyncHelpers';
import {assertPersistedStorageAsync} from '../../src/helpers/storageHelpers';
import {PersistedStorageUnsupportedError} from '../../srcCommon/services/persistentState/persistedState';
import {PrimitiveTask} from './task';
import {TaskStore, TaskStoreSaveEvent} from './taskStoreCommon';

/*
 * Types.
 */

interface TaskStoreSchema extends DBSchema {
  tasks: {
    /** Auto-incrementing primary key for sorting. */
    key: number;
    value: {
      userId: number;
      id: string;
      name: string;
      payload: unknown;
    };
    indexes: {
      userId: number;
      id: string;
    };
  };
}

type TaskStoreDatabase = IDBPDatabase<TaskStoreSchema>;
type TaskStoreName = StoreNames<TaskStoreSchema>;
type TaskStoreTransaction<
  TStoreNames extends Array<TaskStoreName>,
  TMode extends IDBTransactionMode,
> = IDBPTransaction<TaskStoreSchema, TStoreNames, TMode>;
type TaskStoreDatabaseMigration = (
  database: TaskStoreDatabase,
  transaction: TaskStoreTransaction<Array<TaskStoreName>, 'versionchange'>,
) => void;

/*
 * Constants.
 */

const databaseName = 'offlineTasks2';
const databaseMigrations: ReadonlyArray<TaskStoreDatabaseMigration> = [
  (database) => {
    const store = database.createObjectStore('tasks', {autoIncrement: true});
    store.createIndex('userId', 'userId');
    store.createIndex('id', 'id', {unique: true});
  },
];

const defaultReadTimeout = 30 * 1000;
const defaultWriteTimeout = 15 * 1000;

/*
 * State.
 */

let databasePromise: Promise<TaskStoreDatabase> | undefined;
const saveEvents$ = new Subject<TaskStoreSaveEvent>();

/*
 * API.
 */

async function loadTasksAsync(
  userId: number,
  timeout = defaultReadTimeout,
): Promise<ReadonlyArray<PrimitiveTask<string, unknown>>> {
  const database = await findDatabaseAsync();

  return withTimeoutAsync(
    // Filter by user ID and sort by the primary key.
    () => database.getAllFromIndex('tasks', 'userId', userId),
    timeout,
    (elapsedWallTime) => {
      throw new ErrorWithData('Loading tasks from store timed out', {
        elapsedWallTime,
        timeout,
      });
    },
  );
}

async function tryLoadTasksAsync(
  userId: number,
  timeout = defaultReadTimeout,
): Promise<ReadonlyArray<PrimitiveTask<string, unknown>>> {
  try {
    return await loadTasksAsync(userId, timeout);
  } catch (error) {
    if (error instanceof PersistedStorageUnsupportedError) {
      return [];
    }

    throw error;
  }
}

/** Emits when new tasks are saved to the store. */
const taskSaveEvents$ = saveEvents$.asObservable();

async function saveTaskAsync(
  userId: number,
  task: PrimitiveTask<string, unknown>,
  timeout = defaultWriteTimeout,
): Promise<void> {
  const [database] = await Promise.all([findDatabaseAsync(), assertPersistedStorageAsync()]);
  const transaction = database.transaction('tasks', 'readwrite');

  await finishTransactionAsync(transaction, () => transaction.store.put({userId, ...task}), timeout);

  saveEvents$.next({
    userId,
    id: task.id,
    name: task.name,
  });
}

async function removeTaskAsync(taskId: string, timeout = defaultWriteTimeout): Promise<void> {
  const database = await findDatabaseAsync();
  const transaction = database.transaction('tasks', 'readwrite');
  const index = transaction.store.index('id');

  await finishTransactionAsync(
    transaction,
    async () => {
      // Note: a key cursor cannot be used to delete.
      const cursor = await index.openCursor(taskId);
      if (!cursor) {
        return;
      }

      await cursor.delete();
    },
    timeout,
  );
}

async function tryRemoveTaskAsync(taskId: string, timeout = defaultWriteTimeout): Promise<void> {
  try {
    await removeTaskAsync(taskId, timeout);
  } catch (error) {
    if (error instanceof PersistedStorageUnsupportedError) {
      return;
    }

    reportToBugsnag(new ExtendedError('Failed to remove offline task', error));
  }
}

async function closeTaskStoreAsync() {
  if (!databasePromise) {
    return;
  }

  const prevDatabasePromise = databasePromise;
  databasePromise = undefined;

  try {
    const database = await prevDatabasePromise;
    database.close();
  } catch {
    // Ignore.
  }
}

export const taskStore: TaskStore = {
  loadTasksAsync,
  tryLoadTasksAsync,
  taskSaveEvents$,
  saveTaskAsync,
  removeTaskAsync,
  tryRemoveTaskAsync,
  closeTaskStoreAsync,
};

/*
 * Helpers.
 */

async function findDatabaseAsync(): Promise<TaskStoreDatabase> {
  if (typeof indexedDB === 'undefined') {
    throw new PersistedStorageUnsupportedError();
  }

  await assertPersistedStorageAsync();

  if (!databasePromise) {
    databasePromise = openDatabaseAsync();

    // Chrome force-closes long-lasting IDB connections, and our Electron app does clearStorageData() on sign out.
    // https://github.com/localForage/localForage/issues/581
    databasePromise.then((database) => {
      const nativeDatabase = unwrap(database as IDBPDatabase);
      nativeDatabase.addEventListener(
        'close',
        () => {
          // Re-connect when we're called next time to avoid "the database connection is closing" errors.
          databasePromise = undefined;
        },
        {once: true},
      );
    });
  }

  return databasePromise;
}

async function openDatabaseAsync(): Promise<TaskStoreDatabase> {
  return openDB(databaseName, databaseMigrations.length, {
    upgrade: (database, oldVersion, newVersion, transaction) => {
      // Is the database being deleted?
      if (isNull(newVersion)) {
        return;
      }

      // Run the migrations from oldVersion to newVersion.
      databaseMigrations.slice(oldVersion, newVersion).forEach((migrate) => migrate(database, transaction));
    },
    blocking: closeTaskStoreAsync,
  });
}

async function finishTransactionAsync<TStoreNames extends Array<StoreNames<TaskStoreSchema>>>(
  transaction: TaskStoreTransaction<TStoreNames, 'readwrite'>,
  callback: () => Promise<unknown>,
  timeout: number,
): Promise<void> {
  return withTimeoutAsync(
    async () => {
      await callback();
      await transaction.done;
    },
    timeout,
    (elapsedWallTime) => {
      transaction.abort();

      throw new ErrorWithData('Task store transaction timed out', {
        elapsedWallTime,
        timeout,
      });
    },
  );
}
